/**
 * Sweep API - AI-powered document sweeps for issue detection.
 *
 * Provides operations for:
 * - Sweep requests (quote, create, start)
 * - Sweep runs (status, flags, reports)
 * - AI spend management (catalog, quotes, approvals)
 *
 * Types are in ./sweep-types.ts
 * React Query hooks are in ./sweep-hooks.ts
 */

import apiClient, { type RequestConfig } from './client';
import type {
  SweepQuoteRequest,
  SweepQuoteResponse,
  SweepRequestCreate,
  SweepRequestUpdate,
  SweepRequestResponse,
  SweepRequestListResponse,
  SweepRequestQuery,
  SweepRunResponse,
  SweepRunListResponse,
  SweepFlagQuery,
  SweepFlagResponse,
  SweepFlagListResponse,
  SweepFlagReview,
  SweepFlagBulkReview,
  SweepReportResponse,
  CreateReviewQueuesRequest,
  CreateReviewQueuesResponse,
  CatalogSnapshotResponse,
  QuoteRequest,
  QuoteResponse,
  SpendRequestCreate,
  SpendRequestResponse,
  SpendRequestStatus,
  SpendSummary,
} from './sweep-types';

// Re-export all types so existing imports from './sweep' continue to work
export * from './sweep-types';

// Re-export all hooks so existing imports from './sweep' continue to work
export * from './sweep-hooks';

// =============================================================================
// API Endpoints
// =============================================================================

const SWEEP_ENDPOINT = '/sweep';
const AI_ENDPOINT = '/ai';

export const sweepApi = {
  // ---------------------------------------------------------------------------
  // Sweep Quote
  // ---------------------------------------------------------------------------

  /**
   * Get cost estimate for a sweep operation.
   */
  async getQuote(
    matterId: string,
    request: SweepQuoteRequest,
    config?: RequestConfig
  ): Promise<SweepQuoteResponse> {
    const { data } = await apiClient.post<SweepQuoteResponse>(
      `${SWEEP_ENDPOINT}/quote`,
      { ...request, matter_id: matterId },
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Sweep Requests
  // ---------------------------------------------------------------------------

  /**
   * Create a new sweep request.
   */
  async createRequest(
    matterId: string,
    request: SweepRequestCreate,
    config?: RequestConfig
  ): Promise<SweepRequestResponse> {
    const { data } = await apiClient.post<SweepRequestResponse>(
      `${SWEEP_ENDPOINT}/requests`,
      request,
      {
        ...config,
        params: { matter_id: matterId },
      }
    );
    return data;
  },

  /**
   * Get a sweep request by ID.
   */
  async getRequest(
    requestId: string,
    config?: RequestConfig
  ): Promise<SweepRequestResponse> {
    const { data } = await apiClient.get<SweepRequestResponse>(
      `${SWEEP_ENDPOINT}/requests/${requestId}`,
      config
    );
    return data;
  },

  /**
   * List sweep requests for a matter.
   */
  async listRequests(
    matterId: string,
    params?: SweepRequestQuery,
    config?: RequestConfig
  ): Promise<SweepRequestListResponse> {
    const { data } = await apiClient.get<SweepRequestListResponse>(
      `${SWEEP_ENDPOINT}/requests`,
      {
        ...config,
        params: {
          matter_id: matterId,
          ...params,
        } as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Update a sweep request (pending_approval status only).
   */
  async updateRequest(
    requestId: string,
    request: SweepRequestUpdate,
    config?: RequestConfig
  ): Promise<SweepRequestResponse> {
    const { data } = await apiClient.patch<SweepRequestResponse>(
      `${SWEEP_ENDPOINT}/requests/${requestId}`,
      request,
      config
    );
    return data;
  },

  /**
   * Cancel a sweep request.
   */
  async cancelRequest(
    requestId: string,
    config?: RequestConfig
  ): Promise<SweepRequestResponse> {
    const { data } = await apiClient.post<SweepRequestResponse>(
      `${SWEEP_ENDPOINT}/requests/${requestId}/cancel`,
      {},
      config
    );
    return data;
  },

  /**
   * Start execution of an approved sweep request.
   */
  async startRequest(
    requestId: string,
    config?: RequestConfig
  ): Promise<SweepRunResponse> {
    const { data } = await apiClient.post<SweepRunResponse>(
      `${SWEEP_ENDPOINT}/requests/${requestId}/start`,
      {},
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Sweep Runs
  // ---------------------------------------------------------------------------

  /**
   * Get a sweep run by ID.
   */
  async getRun(runId: string, config?: RequestConfig): Promise<SweepRunResponse> {
    const { data } = await apiClient.get<SweepRunResponse>(
      `${SWEEP_ENDPOINT}/runs/${runId}`,
      config
    );
    return data;
  },

  /**
   * List sweep runs for a matter.
   */
  async listRuns(
    matterId: string,
    params?: { limit?: number; offset?: number },
    config?: RequestConfig
  ): Promise<SweepRunListResponse> {
    const { data } = await apiClient.get<SweepRunListResponse>(
      `${SWEEP_ENDPOINT}/runs`,
      {
        ...config,
        params: {
          matter_id: matterId,
          ...params,
        } as unknown as Record<string, string | number | boolean | undefined>,
      }
    );
    return data;
  },

  /**
   * Cancel a running sweep.
   */
  async cancelRun(
    runId: string,
    config?: RequestConfig
  ): Promise<SweepRunResponse> {
    const { data } = await apiClient.post<SweepRunResponse>(
      `${SWEEP_ENDPOINT}/runs/${runId}/cancel`,
      {},
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Sweep Flags
  // ---------------------------------------------------------------------------

  /**
   * List flags for a sweep run.
   */
  async listFlags(
    runId: string,
    params?: Omit<SweepFlagQuery, 'sweep_run_id'>,
    config?: RequestConfig
  ): Promise<SweepFlagListResponse> {
    const { data } = await apiClient.get<SweepFlagListResponse>(
      `${SWEEP_ENDPOINT}/runs/${runId}/flags`,
      {
        ...config,
        params: params as unknown as Record<
          string,
          string | number | boolean | undefined
        >,
      }
    );
    return data;
  },

  /**
   * Get a specific flag by ID.
   */
  async getFlag(
    flagId: string,
    config?: RequestConfig
  ): Promise<SweepFlagResponse> {
    const { data } = await apiClient.get<SweepFlagResponse>(
      `${SWEEP_ENDPOINT}/flags/${flagId}`,
      config
    );
    return data;
  },

  /**
   * Review a single flag (mark as reviewed, set decision, add notes).
   */
  async reviewFlag(
    flagId: string,
    review: SweepFlagReview,
    config?: RequestConfig
  ): Promise<SweepFlagResponse> {
    const { data } = await apiClient.patch<SweepFlagResponse>(
      `${SWEEP_ENDPOINT}/flags/${flagId}`,
      {
        is_reviewed: true,
        review_decision: review.decision,
        review_notes: review.notes,
      },
      config
    );
    return data;
  },

  /**
   * Bulk review multiple flags.
   */
  async bulkReviewFlags(
    runId: string,
    review: SweepFlagBulkReview,
    config?: RequestConfig
  ): Promise<{ reviewed: number; failed: number }> {
    const { data } = await apiClient.post<{ reviewed: number; failed: number }>(
      `${SWEEP_ENDPOINT}/runs/${runId}/flags/bulk-review`,
      review,
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Sweep Reports
  // ---------------------------------------------------------------------------

  /**
   * Get summary report for a sweep run.
   */
  async getReport(
    runId: string,
    config?: RequestConfig
  ): Promise<SweepReportResponse> {
    const { data } = await apiClient.get<SweepReportResponse>(
      `${SWEEP_ENDPOINT}/runs/${runId}/report`,
      config
    );
    return data;
  },

  /**
   * Create review queues from flagged issues.
   */
  async createReviewQueues(
    runId: string,
    request?: CreateReviewQueuesRequest,
    config?: RequestConfig
  ): Promise<CreateReviewQueuesResponse> {
    const { data } = await apiClient.post<CreateReviewQueuesResponse>(
      `${SWEEP_ENDPOINT}/runs/${runId}/create-review-queues`,
      request || {},
      config
    );
    return data;
  },
};

// =============================================================================
// AI Spend Management API
// =============================================================================

export const aiSpendApi = {
  // ---------------------------------------------------------------------------
  // Catalog
  // ---------------------------------------------------------------------------

  /**
   * Get current model catalog.
   */
  async getCurrentCatalog(
    config?: RequestConfig
  ): Promise<CatalogSnapshotResponse> {
    const { data } = await apiClient.get<CatalogSnapshotResponse>(
      `${AI_ENDPOINT}/catalog/current`,
      config
    );
    return data;
  },

  /**
   * Get a specific catalog snapshot.
   */
  async getCatalogSnapshot(
    snapshotId: string,
    config?: RequestConfig
  ): Promise<CatalogSnapshotResponse> {
    const { data } = await apiClient.get<CatalogSnapshotResponse>(
      `${AI_ENDPOINT}/catalog/${snapshotId}`,
      config
    );
    return data;
  },

  /**
   * Refresh the model catalog from providers.
   */
  async refreshCatalog(
    provider: 'openrouter' | 'cerebras_direct' = 'openrouter',
    config?: RequestConfig
  ): Promise<CatalogSnapshotResponse> {
    const { data } = await apiClient.post<CatalogSnapshotResponse>(
      `${AI_ENDPOINT}/catalog/refresh`,
      {},
      {
        ...config,
        params: {
          ...(config?.params ?? {}),
          provider,
        },
      }
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Quotes
  // ---------------------------------------------------------------------------

  /**
   * Request an AI cost quote.
   */
  async getQuote(
    request: QuoteRequest,
    config?: RequestConfig
  ): Promise<QuoteResponse> {
    const { data } = await apiClient.post<QuoteResponse>(
      `${AI_ENDPOINT}/quote`,
      request,
      config
    );
    return data;
  },

  /**
   * Accept a quote.
   */
  async acceptQuote(
    quoteId: string,
    justification?: string,
    config?: RequestConfig
  ): Promise<QuoteResponse> {
    const { data } = await apiClient.post<QuoteResponse>(
      `${AI_ENDPOINT}/quotes/${quoteId}/accept`,
      { justification },
      config
    );
    return data;
  },

  /**
   * Get a quote by ID.
   */
  async getQuoteById(
    quoteId: string,
    config?: RequestConfig
  ): Promise<QuoteResponse> {
    const { data } = await apiClient.get<QuoteResponse>(
      `${AI_ENDPOINT}/quotes/${quoteId}`,
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Spend Requests
  // ---------------------------------------------------------------------------

  /**
   * Create a spend approval request.
   */
  async createSpendRequest(
    request: SpendRequestCreate,
    config?: RequestConfig
  ): Promise<SpendRequestResponse> {
    const { data } = await apiClient.post<SpendRequestResponse>(
      `${AI_ENDPOINT}/spend-requests`,
      request,
      config
    );
    return data;
  },

  /**
   * List spend requests.
   */
  async listSpendRequests(
    params?: {
      status?: SpendRequestStatus;
      limit?: number;
      offset?: number;
    },
    config?: RequestConfig
  ): Promise<{ items: SpendRequestResponse[]; total: number }> {
    const { data } = await apiClient.get<{
      items: SpendRequestResponse[];
      total: number;
    }>(`${AI_ENDPOINT}/spend-requests`, {
      ...config,
      params: params as unknown as Record<
        string,
        string | number | boolean | undefined
      >,
    });
    return data;
  },

  /**
   * Get a spend request by ID.
   */
  async getSpendRequest(
    requestId: string,
    config?: RequestConfig
  ): Promise<SpendRequestResponse> {
    const { data } = await apiClient.get<SpendRequestResponse>(
      `${AI_ENDPOINT}/spend-requests/${requestId}`,
      config
    );
    return data;
  },

  /**
   * Approve a spend request.
   */
  async approveSpendRequest(
    requestId: string,
    reason?: string,
    config?: RequestConfig
  ): Promise<SpendRequestResponse> {
    const { data } = await apiClient.post<SpendRequestResponse>(
      `${AI_ENDPOINT}/spend-requests/${requestId}/approve`,
      { approved: true, reason },
      config
    );
    return data;
  },

  /**
   * Deny a spend request.
   */
  async denySpendRequest(
    requestId: string,
    reason?: string,
    config?: RequestConfig
  ): Promise<SpendRequestResponse> {
    const { data } = await apiClient.post<SpendRequestResponse>(
      `${AI_ENDPOINT}/spend-requests/${requestId}/deny`,
      { approved: false, reason },
      config
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Spend Summary
  // ---------------------------------------------------------------------------

  /**
   * Get spend summary for a matter.
   */
  async getMatterSpendSummary(
    matterId: string,
    params?: { start_date?: string; end_date?: string },
    config?: RequestConfig
  ): Promise<SpendSummary> {
    const { data } = await apiClient.get<SpendSummary>(
      `${AI_ENDPOINT}/spend/matters/${matterId}/summary`,
      {
        ...config,
        params: params as unknown as Record<
          string,
          string | number | boolean | undefined
        >,
      }
    );
    return data;
  },

  /**
   * Get spend summary for tenant.
   */
  async getTenantSpendSummary(
    params?: { start_date?: string; end_date?: string },
    config?: RequestConfig
  ): Promise<SpendSummary> {
    const { data } = await apiClient.get<SpendSummary>(
      `${AI_ENDPOINT}/spend/summary`,
      {
        ...config,
        params: params as unknown as Record<
          string,
          string | number | boolean | undefined
        >,
      }
    );
    return data;
  },
};

export default sweepApi;
